<script>
import { Bar } from 'vue-chartjs'

export default {
    name: 'YearlyProjectsComparision',
    extends: Bar,

    props: {
        yearlyProjectsComparisonData: Array,
        serviceModules: Array,
    },

    data () {
        return {
            graphData: {
                labels: [],
                datasets: [{
                    data: [],
                    backgroundColor: '#97C131',
                    maxBarThickness: 30,
                }],
            },
            graphOptions: {
                plugins: {
                    labels: {
                        render: ({ value }) => {
                            return `${value}`
                        },
                        position: 'outside',
                        textMargin: 5,
                        fontFamily: "Sansation",
                        fontSize: 10,
                        align: 'left',
                        padding: {
                            bottom: 20
                        }
                    }
                },
                layout: {
                    padding: {
                        top: 10,
                        bottom: 20
                    }
                },
                maintainAspectRatio: false,
                responsive: true,
                legend: {
                    display: false,
                },
                indexAxis: 'y',
                scales: {
                    xAxes: [{
                        stacked: true,
                        gridLines: {
                            drawTicks: false,
                            drawOnChartArea: false,
                        },
                        ticks: {
                            padding: 10,
                            maxRotation: 0,
                        },
                    }],
                    yAxes: [{
                        stacked: true,
                        gridLines: {
                            display: false,
                        },
                        ticks: {
                            display: false,
                            min: 0,
                            callback: value => value,
                        },
                    }],
                },
                tooltips: {
                    enabled: false,
                },
            },
            module_colors: {            
                'Valuation': '#97c131',
                'Merger': '#f1563f',
                'Succession Planning': '#323038',
                'Contingency': '#5f6062',
                'Deal Support': '#8b5cf6',
                'Employment Resources': '#9ca3af',
                'Entity Maintenance': '#22c55e',
                'Entity Support': '#06b6d4',
                'Equity Sharing Plan': '#eab308',
                'HR Solutions: Comp': '#0096a6',
                'Seller Advocacy': '#ec4899',
                'Other': '#9ca3af',
            }
        }
    },
        
    computed: {
        // FIXED: Added defensive check (this.rawProjectData && ...) to prevent reading length on undefined
        sourceData() {
            const rawData = this.yearlyProjectsComparisonData;
            if (rawData && rawData.length > 0) {
                return rawData;
            }
            return []
            // // If rawData is null, undefined, or empty, return the mock data
            // return this.mockData;
        },

        // New computed property to transform the flat array into the grouped structure
        processedChartData() {
            const data = this.sourceData;
            
            // 1. Create a lookup map for Module ID -> Service Name
            const moduleIdToServiceMap = new Map();
            this.serviceModules.forEach(serviceDef => {
                serviceDef.moduleIds.forEach(id => {
                    moduleIdToServiceMap.set(id, serviceDef.service);
                });
            });

            // 2. Aggregate counts by year and service
            const groupedByYearAndService = {}; // { 2022: { 'Valuation': 36, 'Contingency': 2, ... }, ... }

            data.forEach(item => {
                const year = item.year_started;
                const serviceName = moduleIdToServiceMap.get(item.module_id) || 'Other'; 
                const count = JSON.parse(item.project_count);
                
                if (!groupedByYearAndService[year]) {
                    groupedByYearAndService[year] = {};
                }
                
                // Aggregate the count for the specific year and service
                groupedByYearAndService[year][serviceName] = 
                    (groupedByYearAndService[year][serviceName] || 0) + count;
            });

            // 3. Format into the final array structure
            const finalOutput = Object.keys(groupedByYearAndService).map(year => {
                const serviceCounts = groupedByYearAndService[year];
                
                const modules = Object.keys(serviceCounts).map(serviceName => ({
                    module_name: serviceName, // This becomes the label in the legend/stack
                    project_count: serviceCounts[serviceName]
                }));

                // const sortedModules = modules.sort((a, b) => {
                //     // Primary sort: Descending by project_count
                //     if (b.project_count !== a.project_count) {
                //         return b.project_count - a.project_count;
                //     }

                //     // Secondary sort (optional, for ties): Alphabetical ascending by service_name
                //     return a.module_name.localeCompare(b.module_name);
                // });
                
                return {
                    effective_year: parseInt(year),
                    // modules: sortedModules
                    modules: modules
                };
            });

            // 4. Sort by year and return
            return finalOutput.sort((a, b) => a.effective_year - b.effective_year);
        },

        // Step 2: Extract unique module names and pivot data for the chart
        transformedData() {
            const data = this.processedChartData;
            
            // 1. Find all unique module names (which are now the aggregated Service names)
            const allModules = data.flatMap(yearData => yearData.modules);
            const moduleNames = [...new Set(allModules.map(m => m.module_name))];
            
            return { 
                groupedData: data,
                moduleKeys: moduleNames 
            };
        },

        // Step 3: PIVOT the processed data into the Chart.js { labels, datasets } format
        chartData() {
            const { groupedData: data, moduleKeys } = this.transformedData;
            
            // The X-axis labels are the years
            const labels = data.map(d => d.effective_year.toString());

            // Create a dataset for each unique module (PIVOTING)
            const datasets = moduleKeys.map(key => ({
                label: key,
                // For each service (key), we find its count across all years
                data: data.map(yearData => {
                    // Look inside the current year's 'modules' array
                    const moduleEntry = yearData.modules.find(m => m.module_name === key);
                    // Return the count, ensuring it's 0 if the service didn't exist that year
                    return moduleEntry ? moduleEntry.project_count : 0;
                }),
                backgroundColor: this.module_colors[key] || this.module_colors.Other,
                borderColor: "white",
                borderWidth: 1,
            }));
            
            return { labels, datasets };
        },
        
        // Step 4: Provides the options object to the <Bar> component (Chart.js V4 syntax)
        chartOptions() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: { 
                        stacked: true, 
                        title: { display: true, text: 'Effective Year' },
                        grid: { display: false },
                    },
                    y: { 
                        stacked: true, 
                        beginAtZero: true,
                        title: { display: true, text: 'Project Count' },
                    }
                },
                
                plugins: {
                    legend: {
                        position: 'top', 
                        labels: { 
                            usePointStyle: true, 
                            padding: 30
                        }
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        bodyFont: { family: "Sansation" },
                        titleFont: { family: "Sansation" },
                    },
                },
            };
        }
    },

    watch: {
        yearlyProjectsComparisonData: {
            handler: () =>  { this.formatData() },
            deep: true,
        },
    },

    mounted () {
        this.formatData();
    },

    methods: {
        formatData () {
            this.renderChart(this.chartData, this.chartOptions)
        }
    }
}
</script>

<style scoped>
    .chart-container {
        height: 100vh;
        width: 100vw;
        background-color: #97C131;
    }
</style>


// WEBPACK FOOTER //
// src/components/charts/yearly_projects_comparison.vue